import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Toaster } from '@/components/common/Toaster';
import { Sidebar } from '@/components/layout/Sidebar';
import { Sheet } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import { useAuthStore } from '@/lib/stores/authStore';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { SessionExpiredOverlay } from '@/components/common/SessionExpiredOverlay';
import { GlobalAccountModal } from '@/components/account/GlobalAccountModal';

export default function RootLayout() {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarExpanded, setIsDesktopSidebarExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { isLoggedIn } = useAuthStore();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans antialiased flex relative">
      {isLoggedIn && (
        <>
          {/* Mobile Sidebar (Sheet) - swipe or tap to open */}
          {isMobile && (
            <Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
              <Sidebar
                isOpen={isMobileSidebarOpen}
                isDesktopExpanded={false}
                setIsOpen={setIsMobileSidebarOpen}
                isMobile={true}
              />
            </Sheet>
          )}

          {/* Desktop Sidebar - hover to expand, overlays content */}
          {!isMobile && (
            <Sidebar
              isOpen={false}
              isDesktopExpanded={isDesktopSidebarExpanded}
              setIsOpen={setIsDesktopSidebarExpanded}
              isMobile={false}
            />
          )}
        </>
      )}

      <div
        className={`flex-1 flex flex-col transition-all duration-250 ease-in-out
        ${isLoggedIn && !isMobile ? 'ml-16' : ''}`}
      >
        {/* Mobile floating menu button - top left */}
        {isLoggedIn && isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className="fixed top-3 left-3 z-40 bg-background/80 backdrop-blur-sm shadow-sm border lg:hidden"
            onClick={() => setIsMobileSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Main content area - no header, content goes to top */}
        <main className="flex-1 pb-[env(safe-area-inset-bottom)] lg:pb-0">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <Toaster />
      {/* Global account modal — opened (optionally to a specific tab) from the
          user menu and every out-of-tokens CTA via `useAccountModalStore`. */}
      {isLoggedIn && <GlobalAccountModal />}
      {/* Global auth-recovery overlay — listens for `session-expired` events */}
      {isLoggedIn && <SessionExpiredOverlay />}
    </div>
  );
}
